import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

/**
 * Selos e pontos de estado (RNF15).
 *
 * Cor de status e vocabulario reservado e nunca viaja sozinha: todo Badge leva
 * texto, e os tons `good/warning/critical` sao os mesmos da StatusPill da
 * tabela de leads. `brand`, `accent` e `info` sao para categoria (Ativo,
 * Utility, Marketing, canal), nao para estado.
 */

export type BadgeTone = 'neutral' | 'brand' | 'accent' | 'info' | 'good' | 'warning' | 'critical' | 'violet';

const TONE: Readonly<Record<BadgeTone, string>> = {
  neutral: 'border-line bg-ink/5 text-ink-muted',
  brand: 'border-brand/40 bg-brand/10 text-brand',
  accent: 'border-accent/40 bg-accent/10 text-accent',
  info: 'border-info/40 bg-info/10 text-info',
  good: 'border-status-good/40 bg-status-good/10 text-status-good',
  warning: 'border-status-warning/50 bg-status-warning/10 text-status-warning',
  critical: 'border-status-critical/40 bg-status-critical/10 text-status-critical',
  violet: 'border-series-7/40 bg-series-7/10 text-series-7',
};

const DOT: Readonly<Record<BadgeTone, string>> = {
  neutral: 'bg-ink-subtle',
  brand: 'bg-brand',
  accent: 'bg-accent',
  info: 'bg-info',
  good: 'bg-status-good',
  warning: 'bg-status-warning',
  critical: 'bg-status-critical',
  violet: 'bg-series-7',
};

export function Badge({
  tone = 'neutral',
  icon,
  dot = false,
  uppercase = false,
  children,
  className = '',
}: {
  readonly tone?: BadgeTone;
  readonly icon?: IconName;
  readonly dot?: boolean;
  readonly uppercase?: boolean;
  readonly children: ReactNode;
  readonly className?: string;
}): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${uppercase ? 'uppercase tracking-wide' : ''} ${TONE[tone]} ${className}`}
    >
      {dot ? <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${DOT[tone]}`} /> : null}
      {icon === undefined ? null : <Icon name={icon} size="xs" />}
      {children}
    </span>
  );
}

/** Ponto colorido isolado, para listas densas onde o Badge inteiro pesaria. */
export function Dot({ tone = 'neutral', className = '' }: { readonly tone?: BadgeTone; readonly className?: string }): JSX.Element {
  return <span aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 rounded-full ${DOT[tone]} ${className}`} />;
}
